import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import Hls from 'hls.js';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Minimize, 
  Settings, Radio, RefreshCw, SkipBack, SkipForward
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { XbotCoverOverlay } from './XbotCoverOverlay';

/**
 * RooamVideoPlayer - Custom branded video player with HLS support
 * Features:
 * - HLS.js for live streaming with proper instance management
 * - Cloudflare iframe fallback for reliability
 * - Auto-play with muted start (browser policy)
 * - Prominent "Tap to Unmute" overlay
 * - Custom controls (no browser defaults)
 * - Quality selector
 * - DVR/rewind during live
 * - Exposes video element via forwardRef for clip creation
 */

// Extract Cloudflare video UID from various URL formats
const extractCloudflareVideoUid = (url) => {
  if (!url) return null;
  // Match patterns like: customer-xxx.cloudflarestream.com/{uid}/manifest/video.m3u8
  // or: videodelivery.net/{uid}/manifest/video.m3u8
  const patterns = [
    /cloudflarestream\.com\/([a-f0-9]+)\/manifest/i,
    /videodelivery\.net\/([a-f0-9]+)\/manifest/i,
    /cloudflarestream\.com\/([a-f0-9]+)\//i,
    /\/([a-f0-9]{32})\/manifest/i,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Extract Cloudflare customer subdomain from URL
const extractCloudflareSubdomain = (url) => {
  if (!url) return null;
  const match = url.match(/customer-([a-z0-9]+)\.cloudflarestream/i);
  return match ? match[1] : null;
};

export const RooamVideoPlayer = forwardRef(function RooamVideoPlayer({
  src,
  isLive = false,
  isVOD = false,
  autoPlay = true,
  muted: initialMuted = true,
  onError,
  onPlay,
  onPause,
  onEnded,
  className = '',
  showControls = true,
  children, // For overlays
}, ref) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsInstanceRef = useRef(null);
  const progressRef = useRef(null);
  const mountedRef = useRef(true);
  const srcRef = useRef(src);

  // Expose video element to parent via ref
  useImperativeHandle(ref, () => ({
    get currentTime() {
      return videoRef.current?.currentTime || 0;
    },
    set currentTime(value) {
      if (videoRef.current) {
        videoRef.current.currentTime = value;
      }
    },
    play() {
      return videoRef.current?.play();
    },
    pause() {
      return videoRef.current?.pause();
    },
    get duration() {
      return videoRef.current?.duration || 0;
    },
    get paused() {
      return videoRef.current?.paused ?? true;
    },
  }));
  
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true); // Always start muted for autoplay
  const [showUnmuteOverlay, setShowUnmuteOverlay] = useState(true);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControlsOverlay, setShowControlsOverlay] = useState(true);
  const [quality, setQuality] = useState('auto');
  const [availableQualities, setAvailableQualities] = useState([]);
  const [isBuffering, setIsBuffering] = useState(true);
  const [liveEdge, setLiveEdge] = useState(0);
  const [isBehindLive, setIsBehindLive] = useState(false);
  const [error, setError] = useState(null);
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const [hlsLoadAttempts, setHlsLoadAttempts] = useState(0);
  const hideControlsTimeout = useRef(null);

  // Track component mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cleanup HLS instance helper
  const cleanupHls = useCallback(() => {
    if (hlsInstanceRef.current) {
      try {
        hlsInstanceRef.current.destroy();
      } catch (e) {
        console.log('HLS cleanup error (safe to ignore):', e);
      }
      hlsInstanceRef.current = null;
    }
  }, []);

  // Initialize HLS with proper instance management
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src || useIframeFallback) return;

    // Track source changes
    srcRef.current = src;
    setError(null);
    setIsBuffering(true);

    const isHLS = src.includes('.m3u8');

    if (isHLS && Hls.isSupported()) {
      // Clean up any existing instance first
      cleanupHls();

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: isLive,
        backBufferLength: isLive ? 30 : 90,
        maxBufferLength: isLive ? 30 : 60,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        // Increase retry attempts for network issues
        manifestLoadingMaxRetry: 6,
        levelLoadingMaxRetry: 6,
        fragLoadingMaxRetry: 6,
        manifestLoadingRetryDelay: 1000,
        levelLoadingRetryDelay: 1000,
        fragLoadingRetryDelay: 1000,
      });

      // Store instance reference immediately
      hlsInstanceRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (!mountedRef.current || srcRef.current !== src) return;
        
        // Get available quality levels
        const levels = hls.levels.map((level, index) => ({
          index,
          height: level.height,
          bitrate: level.bitrate,
          label: `${level.height}p`,
        }));
        setAvailableQualities([{ index: -1, label: 'Auto' }, ...levels]);
        setIsBuffering(false);
        
        // Auto-play muted
        video.muted = true;
        video.play().catch(err => {
          console.log('Initial play failed, retrying...', err);
          setTimeout(() => {
            if (mountedRef.current && video) {
              video.muted = true;
              video.play().catch(() => {});
            }
          }, 500);
        });
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        if (!mountedRef.current) return;
        const level = hls.levels[data.level];
        if (level) {
          setQuality(quality === 'auto' ? 'auto' : `${level.height}p`);
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (!mountedRef.current) return;
        
        console.log('HLS Error:', data.type, data.details, data.fatal);
        
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            // Auto-recover from network errors
            console.log('Network error, attempting recovery...');
            if (hlsLoadAttempts < 3) {
              setHlsLoadAttempts(prev => prev + 1);
              setTimeout(() => {
                if (hlsInstanceRef.current && mountedRef.current) {
                  hlsInstanceRef.current.startLoad();
                }
              }, 2000);
              return;
            } else {
              // Switch to iframe fallback after 3 attempts
              console.log('Max retries reached, switching to iframe fallback');
              setUseIframeFallback(true);
            }
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            console.log('Media error, attempting recovery...');
            hls.recoverMediaError();
            return;
          } else {
            // For other fatal errors, try iframe fallback
            console.log('Fatal HLS error, switching to iframe fallback');
            setUseIframeFallback(true);
          }
          
          if (!useIframeFallback) {
            setError(`Stream error: ${data.type}`);
            onError?.(data);
          }
        }
      });

      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        if (!mountedRef.current) return;
        if (isLive && video.duration) {
          setLiveEdge(video.duration);
        }
      });

      // Cleanup on unmount or src change
      return () => {
        // Only cleanup if this effect's src matches current src
        if (srcRef.current === src) {
          cleanupHls();
        }
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = src;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.play().catch(() => {
        setTimeout(() => {
          if (mountedRef.current && video) {
            video.muted = true;
            video.play().catch(() => {});
          }
        }, 500);
      });
    } else {
      // Direct video file
      video.crossOrigin = 'anonymous';
      video.src = src;
      video.muted = true;
      
      const handleCanPlay = () => {
        if (mountedRef.current) {
          setIsBuffering(false);
          video.play().catch(() => {});
        }
      };
      video.addEventListener('canplay', handleCanPlay);
      video.load();
      
      return () => {
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [src, isLive, onError, useIframeFallback, hlsLoadAttempts, cleanupHls]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video || useIframeFallback) return;

    const handlePlay = () => {
      if (mountedRef.current) {
        setPlaying(true);
        setIsBuffering(false);
        onPlay?.();
      }
    };

    const handlePause = () => {
      if (mountedRef.current) {
        setPlaying(false);
        onPause?.();
      }
    };

    const handleTimeUpdate = () => {
      if (!mountedRef.current) return;
      setCurrentTime(video.currentTime);
      
      if (isLive && liveEdge > 0) {
        setIsBehindLive(video.currentTime < liveEdge - 10);
      }
    };

    const handleDurationChange = () => {
      if (mountedRef.current) {
        setDuration(video.duration);
      }
    };

    const handleProgress = () => {
      if (mountedRef.current && video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };

    const handleWaiting = () => {
      if (mountedRef.current) setIsBuffering(true);
    };
    
    const handlePlaying = () => {
      if (mountedRef.current) {
        setIsBuffering(false);
        setError(null);
      }
    };
    
    const handleEnded = () => {
      if (mountedRef.current) {
        setPlaying(false);
        onEnded?.();
      }
    };
    
    const handleError = (e) => {
      if (!mountedRef.current) return;
      if (video.src && video.error) {
        const errorCode = video.error.code;
        // Don't set error for ABORTED (code 1) - this is normal during source changes
        if (errorCode === 1) return;
        
        if (errorCode === 4) {
          setError('Video format not supported');
        } else if (errorCode === 2) {
          setError('Network error - check your connection');
        } else if (errorCode === 3) {
          setError('Video decode error');
        }
        onError?.(video.error);
      }
    };
    
    const handleLoadedData = () => {
      if (mountedRef.current) {
        setIsBuffering(false);
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('loadeddata', handleLoadedData);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [isLive, liveEdge, onPlay, onPause, onEnded, onError, useIframeFallback]);

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Auto-hide controls
  useEffect(() => {
    if (showControlsOverlay && playing) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControlsOverlay(false);
      }, 3000);
    }

    return () => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, [showControlsOverlay, playing]);

  const handleMouseMove = useCallback(() => {
    setShowControlsOverlay(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    if (playing) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControlsOverlay(false);
      }, 3000);
    }
  }, [playing]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (playing) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  };

  const handleUnmute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      setMuted(false);
    }
    setShowUnmuteOverlay(false);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !muted;
    setMuted(!muted);
    if (!muted === false) {
      setShowUnmuteOverlay(false);
    }
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setMuted(newVolume === 0);
    if (newVolume > 0) {
      video.muted = false;
      setShowUnmuteOverlay(false);
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !progressRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const seekToLive = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = duration;
    setIsBehindLive(false);
  };

  const skip = (seconds) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.max(0, Math.min(duration, video.currentTime + seconds));
  };

  // Helper to detect mobile device
  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  }, []);

  const toggleFullscreen = async () => {
    const container = containerRef.current;
    if (!container) return;

    if (!isFullscreen) {
      // Enter fullscreen
      try {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) {
          container.mozRequestFullScreen();
        } else if (videoRef.current?.webkitEnterFullscreen) {
          videoRef.current.webkitEnterFullscreen();
        }

        // Auto-rotate to landscape on mobile devices
        if (isMobileDevice() && screen.orientation && screen.orientation.lock) {
          try {
            await screen.orientation.lock('landscape');
          } catch (e) {
            // Orientation lock not supported or denied - that's okay
            console.log('Orientation lock not available:', e.message);
          }
        }
      } catch (err) {
        console.error('Fullscreen error:', err);
      }
    } else {
      // Exit fullscreen
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        }

        // Unlock orientation when exiting fullscreen on mobile
        if (isMobileDevice() && screen.orientation && screen.orientation.unlock) {
          try {
            screen.orientation.unlock();
          } catch (e) {
            console.log('Orientation unlock not available:', e.message);
          }
        }
      } catch (err) {
        console.error('Exit fullscreen error:', err);
      }
    }
  };

  const setQualityLevel = (levelIndex) => {
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.currentLevel = levelIndex;
      setQuality(levelIndex === -1 ? 'auto' : availableQualities.find(q => q.index === levelIndex)?.label || 'auto');
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const retryWithHls = () => {
    setUseIframeFallback(false);
    setHlsLoadAttempts(0);
    setError(null);
  };

  // If no source, show placeholder
  if (!src) {
    return (
      <div className={`relative bg-black flex items-center justify-center ${className}`}>
        <div className="text-center text-white">
          <Radio className="w-16 h-16 mx-auto mb-4 text-zinc-500 animate-pulse" />
          <p className="text-lg">Waiting for stream...</p>
        </div>
        {children}
      </div>
    );
  }

  // Cloudflare iframe fallback - guaranteed playback
  if (useIframeFallback) {
    const videoUid = extractCloudflareVideoUid(src);
    const subdomain = extractCloudflareSubdomain(src);
    
    if (videoUid && subdomain) {
      // Use the actual muted state to build iframe URL - key forces reload when muted changes
      const iframeSrc = `https://customer-${subdomain}.cloudflarestream.com/${videoUid}/iframe?autoplay=true&muted=${muted}&preload=auto`;
      
      const handleIframeUnmute = () => {
        setMuted(false);
        setShowUnmuteOverlay(false);
      };
      
      return (
        <div
          ref={containerRef}
          className={`relative bg-black overflow-hidden ${className}`}
        >
          {/* Key changes when muted state changes, forcing iframe to reload with new audio setting */}
          <iframe
            key={`cf-iframe-${muted}`}
            src={iframeSrc}
            className="w-full h-full"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ border: 'none' }}
          />
          
          {/* Unmute overlay for iframe */}
          {muted && showUnmuteOverlay && (
            <div 
              className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer z-20"
              onClick={handleIframeUnmute}
            >
              <div className="bg-white/20 backdrop-blur-md rounded-full p-6 hover:bg-white/30 transition-all hover:scale-110 shadow-2xl">
                <VolumeX className="w-10 h-10 text-white" />
              </div>
              <div className="absolute bottom-20 text-white text-center">
                <p className="text-xl font-bold drop-shadow-lg">Tap to Unmute</p>
                <p className="text-sm text-white/80 mt-1">Click anywhere to enable sound</p>
              </div>
            </div>
          )}
          
          {/* Option to retry with HLS player */}
          <div className="absolute top-2 right-2 z-30">
            <Button
              variant="ghost"
              size="sm"
              onClick={retryWithHls}
              className="text-white/70 hover:text-white hover:bg-white/20 text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Switch Player
            </Button>
          </div>

          {/* Cover XbotGo burn-in watermark until a sponsorship deal is in place */}
          <XbotCoverOverlay />

          {children}
        </div>
      );
    }
  }

  // Error state with retry option
  if (error && !useIframeFallback) {
    const videoUid = extractCloudflareVideoUid(src);
    const subdomain = extractCloudflareSubdomain(src);
    const canUseIframe = videoUid && subdomain;
    
    return (
      <div className={`relative bg-gradient-to-br from-zinc-900 via-zinc-900 to-black flex items-center justify-center ${className}`}>
        <div className="text-center text-white max-w-md px-4">
          {/* Rooam Logo - LARGE and Center of Attention */}
          <div className="mb-8">
            <img 
              src="/rooam-logo.png" 
              alt="Rooam Sports" 
              className="h-32 sm:h-40 lg:h-48 mx-auto drop-shadow-2xl"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <p className="text-lg font-medium mb-2">
            {isVOD ? 'Replay Unavailable' : 'Stream Error'}
          </p>
          <p className="text-zinc-500 text-sm mb-6">
            {isVOD 
              ? 'This replay is no longer available.'
              : error}
          </p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => {
                setError(null);
                setHlsLoadAttempts(0);
                if (videoRef.current) {
                  videoRef.current.load();
                }
              }}
              variant="outline"
              className="border-zinc-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            {canUseIframe && (
              <Button 
                onClick={() => setUseIframeFallback(true)}
                variant="default"
                className="bg-cyan-500 hover:bg-cyan-400 text-black"
              >
                Backup Player
              </Button>
            )}
          </div>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative bg-black overflow-hidden group ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => playing && setShowControlsOverlay(false)}
      onClick={(e) => {
        if (e.target === containerRef.current || e.target === videoRef.current) {
          togglePlay();
        }
      }}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        crossOrigin="anonymous"
        playsInline
        muted
        autoPlay
      />

      {/* Buffering Indicator */}
      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-3" />
            <p className="text-white text-sm">
              {isLive ? 'Connecting to live stream...' : 'Loading video...'}
            </p>
          </div>
        </div>
      )}

      {/* Prominent Unmute Overlay - Shows when muted and video is playing */}
      {muted && showUnmuteOverlay && playing && !isBuffering && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer z-20"
          onClick={(e) => {
            e.stopPropagation();
            handleUnmute();
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          <div className="relative flex flex-col items-center">
            <div className="bg-white/20 backdrop-blur-md rounded-full p-6 hover:bg-white/30 transition-all hover:scale-110 shadow-2xl">
              <VolumeX className="w-10 h-10 text-white" />
            </div>
            <div className="mt-4 text-center">
              <p className="text-white text-xl font-bold drop-shadow-lg">Tap to Unmute</p>
              <p className="text-white/80 text-sm mt-1">Sound is muted</p>
            </div>
          </div>
        </div>
      )}

      {/* Overlays (scoreboard, watermark) - passed as children */}
      {children}

      {/* Custom Controls */}
      {showControls && (
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 z-30 ${
            showControlsOverlay ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Progress Bar */}
          <div className="px-4 pt-8">
            <div
              ref={progressRef}
              className="relative h-1 bg-white/30 rounded-full cursor-pointer group/progress"
              onClick={handleSeek}
            >
              {/* Buffered */}
              <div
                className="absolute h-full bg-white/40 rounded-full"
                style={{ width: `${(buffered / duration) * 100}%` }}
              />
              {/* Progress */}
              <div
                className="absolute h-full bg-rooam-red rounded-full"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              {/* Scrubber */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
                style={{ left: `${(currentTime / duration) * 100}%`, transform: 'translate(-50%, -50%)' }}
              />
              {/* Live Indicator */}
              {isLive && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full animate-pulse"
                  style={{ right: 0, transform: 'translate(50%, -50%)' }}
                />
              )}
            </div>
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3">
            {/* Left Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Play/Pause */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 h-8 w-8 sm:h-9 sm:w-9"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                data-testid="video-play-pause"
              >
                {playing ? <Pause className="h-4 w-4 sm:h-5 sm:w-5" /> : <Play className="h-4 w-4 sm:h-5 sm:w-5" />}
              </Button>

              {/* Skip Back (VOD only, hidden on mobile) */}
              {!isLive && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 h-8 w-8 sm:h-9 sm:w-9 hidden sm:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    skip(-10);
                  }}
                >
                  <SkipBack className="h-4 w-4" />
                </Button>
              )}

              {/* Skip Forward (VOD only, hidden on mobile) */}
              {!isLive && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 h-8 w-8 sm:h-9 sm:w-9 hidden sm:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    skip(10);
                  }}
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              )}

              {/* Volume - simplified on mobile */}
              <div className="flex items-center gap-1 sm:gap-2 group/volume">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 h-8 w-8 sm:h-9 sm:w-9"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                    if (muted) setShowUnmuteOverlay(false);
                  }}
                  data-testid="video-mute-toggle"
                >
                  {muted || volume === 0 ? <VolumeX className="h-4 w-4 sm:h-5 sm:w-5" /> : <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />}
                </Button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={muted ? 0 : volume}
                  onChange={handleVolumeChange}
                  onClick={(e) => e.stopPropagation()}
                  className="w-0 group-hover/volume:w-20 transition-all duration-200 accent-rooam-red hidden sm:block"
                />
              </div>

              {/* Time Display - more prominent */}
              <span className="text-white text-xs sm:text-sm tabular-nums ml-1 sm:ml-2 font-medium">
                {formatTime(currentTime)} / {isLive ? 'LIVE' : formatTime(duration)}
              </span>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Go Live Button (when behind live) */}
              {isLive && isBehindLive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white bg-red-600 hover:bg-red-700 h-6 sm:h-7 text-[10px] sm:text-xs px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    seekToLive();
                  }}
                >
                  <Radio className="h-3 w-3 mr-1" />
                  LIVE
                </Button>
              )}

              {/* Live Badge */}
              {isLive && !isBehindLive && (
                <div className="flex items-center gap-1 bg-red-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs text-white">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse" />
                  LIVE
                </div>
              )}

              {/* VOD Badge */}
              {isVOD && (
                <div className="flex items-center gap-1 bg-zinc-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs text-white">
                  REPLAY
                </div>
              )}

              {/* Quality Selector - hidden on mobile */}
              {availableQualities.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 h-7 sm:h-8 text-xs hidden sm:flex"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      {quality === 'auto' ? 'Auto' : quality}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                    {availableQualities.map((q) => (
                      <DropdownMenuItem
                        key={q.index}
                        onClick={() => setQualityLevel(q.index)}
                        className={`text-white hover:bg-zinc-800 cursor-pointer ${
                          (q.index === -1 && quality === 'auto') || q.label === quality
                            ? 'bg-rooam-red/20 text-rooam-red'
                            : ''
                        }`}
                      >
                        {q.label}
                        {q.index === -1 && ' (Recommended)'}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Fullscreen - hidden on mobile since we have Expand button below */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 h-9 w-9 hidden sm:flex"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                data-testid="video-fullscreen"
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Click to play overlay (only for VOD when paused and not showing unmute) */}
      {!playing && !isBuffering && !isLive && !showUnmuteOverlay && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
          onClick={togglePlay}
        >
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Cover XbotGo burn-in watermark until a sponsorship deal is in place */}
      <XbotCoverOverlay />
    </div>
  );
});

export default RooamVideoPlayer;
